import React, { useState } from 'react';
import { FiAlertCircle, FiX, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './ReportMessageModal.css';

/**
 * ReportMessageModal Component
 *
 * Props:
 * - isOpen (boolean) - Controls modal visibility
 * - message (object) - The DM message object to report
 * - onClose (function) - Callback when modal is closed
 * - onReportSubmitted (function) - Callback after successful report
 *
 * Features:
 * - Report reason dropdown (harassment, spam, inappropriate, scam, other)
 * - Description textarea (optional, max 500 chars)
 * - Submit button calls report_dm_message() RPC
 * - Prevent duplicate reports
 * - Success/error messages
 */
export default function ReportMessageModal({ isOpen, message, onClose, onReportSubmitted }) {
    const { supabase } = useAuth();
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const reportReasons = [
        { value: 'harassment', label: 'Harassment or Bullying' },
        { value: 'spam', label: 'Spam or Advertisement' },
        { value: 'inappropriate', label: 'Inappropriate Content' },
        { value: 'scam', label: 'Scam or Fraud' },
        { value: 'other', label: 'Other' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!reason) {
            setError('Please select a reason for reporting');
            return;
        }

        if (!message?.id) {
            setError('Invalid message');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // Call the report_dm_message RPC function
            const { data, error: rpcError } = await supabase.rpc('report_dm_message', {
                p_message_id: message.id,
                p_reason: reason,
                p_description: description.trim() || null
            });

            if (rpcError) {
                // Check for duplicate report error
                if (rpcError.message?.includes('already reported') || rpcError.code === '23505') {
                    setError('You have already reported this message');
                    return;
                }
                throw rpcError;
            }

            if (!data?.success) {
                throw new Error(data?.message || 'Failed to submit report');
            }

            // Success
            setSuccess(true);
            console.log('[ReportMessageModal] Report submitted successfully:', data);

            // Close modal after 1.5 seconds
            setTimeout(() => {
                if (onReportSubmitted) {
                    onReportSubmitted();
                }
                handleClose();
            }, 1500);

        } catch (err) {
            console.error('[ReportMessageModal] Error submitting report:', err);
            setError(err.message || 'Failed to submit report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form
        setReason('');
        setDescription('');
        setError(null);
        setSuccess(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="report-modal-overlay" onClick={handleClose}>
            <div className="report-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="report-modal-header">
                    <h3>
                        <FiAlertCircle />
                        Report Message
                    </h3>
                    <button
                        className="close-btn"
                        onClick={handleClose}
                        disabled={submitting}
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </div>

                {/* Success State */}
                {success ? (
                    <div className="report-success">
                        <FiCheck className="success-icon" />
                        <h4>Report Submitted</h4>
                        <p>Thank you for helping keep our community safe. Our moderation team will review this report.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {/* Message Preview */}
                        <div className="message-preview">
                            <div className="preview-label">Reporting message:</div>
                            <div className="preview-content">
                                {message?.content?.substring(0, 100)}
                                {message?.content?.length > 100 && '...'}
                            </div>
                            <div className="preview-meta">
                                From: {message?.sender?.display_name || message?.sender?.username}
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="report-error">
                                <FiAlertCircle />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Reason Dropdown */}
                        <div className="form-group">
                            <label htmlFor="report-reason">
                                Reason for Report <span className="required">*</span>
                            </label>
                            <select
                                id="report-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={submitting}
                                required
                                className="form-select"
                            >
                                <option value="">Select a reason...</option>
                                {reportReasons.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Description Textarea */}
                        <div className="form-group">
                            <label htmlFor="report-description">
                                Additional Details (Optional)
                            </label>
                            <textarea
                                id="report-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Provide any additional context that may help moderators..."
                                maxLength={500}
                                disabled={submitting}
                                className="form-textarea"
                                rows={4}
                            />
                            <div className="char-count">
                                {description.length} / 500
                            </div>
                        </div>

                        {/* Info Notice */}
                        <div className="report-info">
                            <FiAlertCircle />
                            <span>
                                Reports are anonymous. The user will not be notified that you reported them.
                            </span>
                        </div>

                        {/* Actions */}
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={handleClose}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-danger"
                                disabled={submitting || !reason}
                            >
                                {submitting ? (
                                    <>
                                        <div className="spinner-small" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <FiAlertCircle />
                                        Submit Report
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
